/**
 * Tela inicial: o aviso do que está vencendo e a grade dos módulos liberados.
 *
 * Papel: desenha um cartão, de `CARTOES_DA_TELA_INICIAL`, para cada um cuja
 * chave (ou uma das `chavesQueLiberamCartao`) o `useAcesso` permite ver, e as
 * mensagens de espera, de falha e de nenhum módulo liberado.
 *
 * Como usar: o filtro passa por `acessoAlcanca` com nível `'ver'`, que testa o
 * papel de administrador antes da lista de permissões. Trocar por uma leitura
 * direta de `acesso.permissoes` esconderia todos os cartões de quem administra,
 * porque o administrador chega com a lista vazia.
 *
 * O cartão escondido não é a proteção do módulo. Quem digitar o endereço de um
 * módulo não liberado chega à página; o que o impede de ver o dado é o 403 da
 * Function, que chama `exigirPermissao` antes de consultar o banco.
 *
 * O aviso de vencimento não fica mais nesta tela: ele é o sino da barra
 * superior (`BotaoDeVencimentos`) e a caixa que o sino abre, ambos lendo
 * `useItensVencendo`. A conferência de quem vê quais módulos é daquele hook, e
 * não daqui, e a grade de módulos não espera por ele nem exibe erro por causa
 * dele.
 */
import { ROTULOS_MODULO, acessoAlcanca } from '@concierge/shared';
import { type ReactNode } from 'react';
import { Navigate } from 'react-router';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import { CartaoDeModulo } from '@/shared/ui/cartao-de-modulo';
import { GradeDeCartoes } from '@/shared/ui/grade-de-cartoes';
import { Vazio } from '@/shared/ui/vazio';
import { CARTOES_DA_TELA_INICIAL, chavesQueLiberamCartao } from './cartoes-da-tela-inicial';
import { soTemSolicitacaoDeCompra } from './so-solicitacao-de-compra';

/**
 * Renderiza a grade de módulos da tela inicial.
 *
 * Enquanto o acesso é consultado, nenhum cartão aparece — mostrar a grade
 * inteira e depois recolhê-la faria quem tem um módulo só ver os outros nove
 * sumirem. Na falha, também nenhum cartão aparece, pelo mesmo motivo da
 * `RotaDeAdministrador`: não saber o acesso não é razão para supor que ele
 * existe.
 *
 * @returns O aviso da situação em que a consulta do acesso está, ou o aviso de
 *   vencimentos seguido da grade.
 */
export function PaginaInicial() {
  const { data: acesso, error: erro, isPending, isFetching, refetch } = useAcesso();

  /** Refaz a consulta do acesso depois de uma falha. */
  function handleTentarDeNovo() {
    void refetch();
  }

  // Quem só tem a solicitação de compra não tem outro destino na grade: a tela
  // dele é a única que pode abrir, e chegar a ela direto poupa um passo a cada
  // entrada.
  if (acesso !== undefined && soTemSolicitacaoDeCompra(acesso)) {
    return <Navigate to="/solicitar-compra" replace />;
  }

  let conteudo: ReactNode;

  if (isPending) {
    conteudo = <p className="text-sm text-muted-foreground">Carregando seus módulos…</p>;
  } else if (erro) {
    conteudo = (
      <Vazio
        mensagem={
          <>
            <p>Não foi possível carregar seus módulos</p>
            <p className="mt-1.5">
              {erro instanceof ErroDaApi
                ? erro.message
                : 'A consulta falhou antes de chegar ao servidor.'}
            </p>
          </>
        }
        rotuloAcao={isFetching ? 'Tentando…' : 'Tentar de novo'}
        onAcao={handleTentarDeNovo}
        acaoDesabilitada={isFetching}
      />
    );
  } else {
    const visiveis = CARTOES_DA_TELA_INICIAL.filter((cartao) =>
      chavesQueLiberamCartao(cartao).some((chave) => acessoAlcanca(acesso, chave, 'ver')),
    );

    conteudo =
      visiveis.length === 0 ? (
        <Vazio
          mensagem={
            <>
              <p>Nenhum módulo liberado para você</p>
              <p className="mx-auto mt-1.5 max-w-md">
                Sua entrada ficou registrada. Peça a quem administra o sistema que libere os
                módulos de que você precisa; eles aparecem aqui depois que você recarregar a
                página.
              </p>
            </>
          }
        />
      ) : (
        <GradeDeCartoes centralizada>
          {visiveis.map((cartao) => (
            <li key={cartao.chave}>
              <CartaoDeModulo
                rota={cartao.rota}
                nome={ROTULOS_MODULO[cartao.chave]}
                Icone={cartao.Icone}
                className="h-full"
              />
            </li>
          ))}
        </GradeDeCartoes>
      );
  }

  return <div className="flex flex-1 flex-col justify-center py-8">{conteudo}</div>;
}
